import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  FileText, Package, ShieldCheck, Warning, Scales, Briefcase, CurrencyDollar,
  CalendarBlank, ChartBar, ClipboardText, ChatCircleDots, Eye, EyeSlash, PencilSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";
import { ScoreBit, Mini } from "./_shared";

// Helpers — small UI bits used throughout
const Section = ({ icon: Icon, title, count, children, testid }) => (
  <div className="border-t border-slate-100 pt-4 mt-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2.5">
      <h3 className="flex items-center gap-2 text-xs font-mono-alt uppercase tracking-widest text-[#1E5CBA]">
        {Icon && <Icon size={14} weight="fill" />}
        {title}
      </h3>
      {typeof count === "number" && (
        <span className="font-mono-alt text-[10px] uppercase tracking-wider rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
          {count} record{count === 1 ? "" : "s"}
        </span>
      )}
    </div>
    {children}
  </div>
);

const Empty = ({ msg }) => <div className="py-3 text-xs text-slate-400">{msg}</div>;

const StatusChip = ({ s }) => {
  const map = {
    active: "bg-emerald-100 text-emerald-700", approved: "bg-emerald-100 text-emerald-700",
    paid: "bg-emerald-100 text-emerald-700", completed: "bg-emerald-100 text-emerald-700",
    deployed: "bg-blue-100 text-blue-700", rented: "bg-blue-100 text-blue-700",
    pending: "bg-amber-100 text-amber-700", under_review: "bg-amber-100 text-amber-700",
    submitted: "bg-amber-100 text-amber-700",
    returned_for_correction: "bg-blue-100 text-blue-700",
    rejected: "bg-rose-100 text-rose-700", lost: "bg-rose-100 text-rose-700",
    cancelled: "bg-zinc-200 text-zinc-700", closed: "bg-zinc-200 text-zinc-700",
    in_store: "bg-slate-100 text-slate-700",
  };
  const tone = map[s] || "bg-slate-100 text-slate-700";
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono-alt text-[10px] uppercase tracking-wider ${tone}`}>{s?.replace(/_/g, " ") || "—"}</span>;
};

// Clickable record row — opens RecordInspector with full record details
const ClickRow = ({ record, title, subtitle, inspector, testid, children }) => (
  <tr
    onClick={() => inspector.open(record, { title, subtitle })}
    className="cursor-pointer hover:bg-slate-50/70 transition-colors"
    data-testid={testid}
  >
    {children}
  </tr>
);

// ── Defense submission form ─────────────────────────────────
// Available when the logged-in employee has an open warning issued to them.
function DefenseFormDialog({ warning, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/hr/defenses", {
        warning_id: warning.id,
        staff_id: warning.staff_id,
        response: body,
        response_date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Defense submitted — HR will review");
      setOpen(false); setBody(""); onSubmitted?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`defense-btn-${warning.id}`}>
          <Scales size={12} className="mr-1.5" /> Submit Defense
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`defense-dialog-${warning.id}`}>
        <DialogHeader>
          <DialogTitle>Defense response</DialogTitle>
          <DialogDescription>
            You are submitting a written defense for the warning dated {formatDate(warning.issue_date)} ({warning.warning_type}).
            HR / Management will review your response.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
            <div className="font-mono-alt uppercase tracking-wider text-amber-700 mb-1">Original warning reason</div>
            <div className="text-slate-700">{warning.reason || "—"}</div>
          </div>
          <div>
            <Label>Your defense / response *</Label>
            <textarea
              required
              minLength={20}
              rows={6}
              className="w-full rounded-md border border-slate-200 p-2 text-sm"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Provide your written response — circumstances, mitigating context, any evidence references…"
              data-testid={`defense-body-${warning.id}`}
            />
            <div className="mt-1 text-[10px] text-slate-500">Min. 20 characters. Defense is linked to this warning and visible to HR + Management.</div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || body.length < 20} className="btn-primary-blue" data-testid={`defense-submit-${warning.id}`}>
              {busy ? "Submitting…" : "Submit Defense"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Score breakdown component ──────────────────────────────
function ScoreBreakdown({ breakdown, overall, rating }) {
  const tiles = [
    { key: "task_completion_20", label: "Task Completion", weight: 20 },
    { key: "task_on_time_12",    label: "On-time Delivery", weight: 12 },
    { key: "task_quality_8",     label: "Quality",          weight: 8 },
    { key: "effort_20",          label: "Effort & Timesheets", weight: 20 },
    { key: "attendance_18",      label: "Attendance",        weight: 18 },
    { key: "discipline_12",      label: "Discipline",        weight: 12 },
    { key: "contract_10",        label: "Contract Status",   weight: 10 },
  ];
  return (
    <div className="dc-card p-3 mb-3 bg-slate-50/50" data-testid="hr-perf-breakdown">
      <div className="overline mb-2 flex flex-wrap items-center gap-2">
        <span>Weighted score breakdown</span>
        <span className="rounded bg-white px-2 py-0.5 font-mono-alt tabular-nums text-[11px] text-slate-700 border border-slate-200">{overall}/100</span>
        <span className="text-[#1E5CBA] font-bold uppercase tracking-wider text-[11px]">{(rating || "").replace(/_/g, " ")}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs min-w-0">
        {tiles.map((t) => {
          const c = breakdown?.[t.key] || { weight: t.weight, score: 0, raw: 0, sources: {} };
          const pct = Math.min(100, ((c.score ?? 0) / t.weight) * 100);
          return (
            <div key={t.key} className="rounded-md border border-slate-200 bg-white p-2 min-w-0" data-testid={`hr-perf-tile-${t.key}`}>
              <div className="text-[10px] text-slate-500 truncate" title={t.label}>{t.label} · {t.weight}%</div>
              <div className="font-mono-alt text-sm font-bold tabular-nums text-slate-900">
                {c.score ?? 0}<span className="text-[10px] text-slate-400">/{t.weight}</span>
              </div>
              <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-[#1E5CBA]" style={{ width: `${pct}%` }} />
              </div>
              {/* Source numbers behind the score */}
              <div className="mt-1.5 text-[9px] leading-tight text-slate-500 truncate" title={JSON.stringify(c.sources || {})}>
                raw {c.raw ?? 0}
                {c.sources && Object.keys(c.sources).length > 0 && (
                  <span> · {Object.entries(c.sources).slice(0, 2).map(([k, v]) => `${k}:${typeof v === "boolean" ? (v ? "✓" : "✗") : v}`).join(", ")}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────
export default function StaffFullProfile({ staffId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const inspector = useRecordInspector();

  const load = () => {
    setErr(null);
    api.get(`/hr/staff/${staffId}/profile`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [staffId]);

  if (err) return <div className="p-8 text-sm text-red-600" data-testid="profile-error">{err}</div>;
  if (!data) return <div className="p-8 text-sm text-slate-500">Loading profile…</div>;

  const s = data.staff;
  const score = data.overall_score;
  const scoreColor = score >= 85 ? "text-emerald-600" : score >= 70 ? "text-blue-600" : score >= 50 ? "text-amber-600" : "text-red-600";
  const isFull = data.viewer?.is_full_viewer;
  const isSelf = data.viewer?.is_self;
  const ss = data.salary_summary || {};
  const asum = data.asset_summary || {};
  const esum = data.ethics_summary || {};
  const tbreak = data.task_status_breakdown || {};
  const effort = data.performance?.effort || {};
  const cr = data.connected_records || {};

  const exportPDF = async () => {
    try {
      const res = await api.get(`/hr/staff/${s.id}/profile.pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `profile_${s.employee_code || s.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Profile PDF downloaded with DESCON letterhead.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      {/* Header */}
      <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-display text-xl sm:text-2xl font-semibold">{s.full_name}</h2>
          <p className="text-xs sm:text-sm text-slate-500">{s.employee_code || "—"} · {s.designation || "—"} · {s.department || "—"}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <StatusChip s={s.status || "active"} />
            {isSelf && <span className="font-mono-alt text-[10px] uppercase tracking-wider rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 border border-blue-200">Self view</span>}
            {!isFull && !isSelf && <span className="font-mono-alt text-[10px] uppercase tracking-wider rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 border border-amber-200">Limited view</span>}
            {isFull && <span className="font-mono-alt text-[10px] uppercase tracking-wider rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 border border-emerald-200">Full access</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 self-start">
          <AuditTrailDrawer entity="hr_staff" entityId={s.id} label={`${s.full_name} audit`} />
          <Button size="sm" onClick={exportPDF} data-testid="export-pdf-btn"><FileText size={14} className="mr-1.5" /> Export PDF</Button>
        </div>
      </div>

      <div id="staff-profile-print" className="min-w-0 max-w-full overflow-x-hidden">
        {/* ── Score card ─────────────────────────────────── */}
        <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 sm:p-5 mb-3" data-testid="hr-score-card">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
            <div className="shrink-0">
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Overall HR Score</div>
              <div className={`score text-5xl sm:text-6xl font-display font-bold ${scoreColor}`} data-testid="hr-overall-score">{score}</div>
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-400 mt-1" data-testid="hr-score-label">
                {data.score_label || `${score}/100`}
              </div>
            </div>
            <div className="flex-1 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <ScoreBit label="Task Completion" pct={data.performance?.completion_rate} weight="20%" />
              <ScoreBit label="On-time Delivery" pct={data.performance?.on_time_rate} weight="12%" />
              <ScoreBit label="Attendance" pct={data.performance?.attendance?.rate} weight="18%" />
              <ScoreBit label="Discipline" pct={100 - (data.performance?.discipline?.penalty || 0)} weight="12%" />
            </div>
          </div>
        </div>

        {/* ── Basic profile ──────────────────────────────── */}
        <h2 className="font-display text-base font-semibold mt-4 mb-2">Profile</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div><strong className="text-slate-500">Email:</strong> {s.email || "—"}</div>
          <div><strong className="text-slate-500">Phone:</strong> {s.phone || "—"}</div>
          <div><strong className="text-slate-500">Employment:</strong> {s.staff_type?.replace(/_/g, " ") || "—"}</div>
          <div><strong className="text-slate-500">Project:</strong> {s.project_id || "—"}</div>
          <div><strong className="text-slate-500">Joined:</strong> {formatDate(s.join_date)}</div>
          <div><strong className="text-slate-500">Salary:</strong> {isFull || isSelf ? formatCurrency(s.salary) : "—"}</div>
          <div><strong className="text-slate-500">Status:</strong> {s.status || "active"}</div>
          <div><strong className="text-slate-500">End date:</strong> {s.end_date ? formatDate(s.end_date) : "—"}</div>
        </div>

        {/* ── Connected Records inventory ────────────────── */}
        <Section icon={ClipboardText} title="Connected Records (cross-module)" testid="sec-connected">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2 text-xs">
            {[
              ["Contracts", cr.contracts],
              ["Salary Reqs", cr.salary_requests],
              ["Receipts", cr.payroll_receipts],
              ["Warnings", cr.warnings],
              ["Defenses", cr.defenses],
              ["Leaves", cr.leaves],
              ["Separations", cr.separations],
              ["Tasks", cr.tasks],
              ["Timesheets", cr.timesheets],
              ["Assets Held", cr.assets_held],
              ["Asset Reqs", cr.asset_requests],
              ["Movements", cr.asset_movements],
              ["Ethics", cr.ethics_cases],
              ["Audit", cr.audit_entries],
            ].map(([lbl, val]) => (
              <div key={lbl} className="rounded-md border border-slate-200 bg-white px-2 py-1.5">
                <div className="text-[9px] uppercase tracking-wider text-slate-500 truncate">{lbl}</div>
                <div className="font-mono-alt text-sm font-bold tabular-nums text-slate-900">{val ?? 0}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Salary History ─────────────────────────────── */}
        {(isFull || isSelf) && (
        <Section icon={CurrencyDollar} title="Salary History" count={data.salary_requests.length} testid="sec-salary">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 mb-3 text-sm">
            <Mini label="Months Paid"   value={ss.months_paid ?? 0} color="text-emerald-600" />
            <Mini label="Total Gross"   value={formatCurrency(ss.total_gross || 0)} />
            <Mini label="Total Net"     value={formatCurrency(ss.total_net || 0)} color="text-emerald-600" />
            <Mini label="Pending"       value={ss.pending_count ?? 0} color={ss.pending_count ? "text-amber-600" : "text-slate-600"} />
            <Mini label="Rejected"      value={ss.rejected_count ?? 0} color={ss.rejected_count ? "text-rose-600" : "text-slate-600"} />
            <Mini label="Receipts"      value={ss.receipts_total ?? 0} />
          </div>
          {data.salary_requests.length === 0 ? <Empty msg="No salary requests on record." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
                  <tr><th className="px-2 py-1.5 text-left">Period</th><th className="px-2 py-1.5 text-left">Gross</th><th className="px-2 py-1.5 text-left">Net</th><th className="px-2 py-1.5 text-left">Status</th><th className="px-2 py-1.5 text-left">Submitted</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.salary_requests.map((r) => (
                    <ClickRow key={r.id} record={r} title={`Salary · ${r.period_month}`} subtitle={`${r.status} · ${formatCurrency(r.gross_amount)}`} inspector={inspector} testid={`salary-row-${r.id}`}>
                      <td className="px-2 py-1.5 font-mono-alt">{r.period_month}</td>
                      <td className="px-2 py-1.5 tabular-nums">{formatCurrency(r.gross_amount)}</td>
                      <td className="px-2 py-1.5 tabular-nums">{formatCurrency(r.net_amount)}</td>
                      <td className="px-2 py-1.5"><StatusChip s={r.status} /></td>
                      <td className="px-2 py-1.5 text-slate-500">{r.created_at?.slice(0, 10)}</td>
                    </ClickRow>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.payroll_receipts.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 mb-1">Payroll Receipts · {data.payroll_receipts.length}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {data.payroll_receipts.slice(0, 6).map((r, i) => (
                  <button
                    key={r.id || i}
                    onClick={() => inspector.open(r, { title: `Receipt ${r.receipt_no || ""}`, subtitle: r.period_month })}
                    className="rounded-md border border-slate-200 bg-white p-2 text-left hover:bg-slate-50"
                    data-testid={`receipt-${r.id || i}`}
                  >
                    <div className="font-mono-alt text-[10px] uppercase text-slate-500 tracking-wider">{r.period_month || "—"}</div>
                    <div className="font-mono-alt text-sm font-bold tabular-nums">{formatCurrency(r.net_amount || r.amount || 0)}</div>
                    <div className="text-[10px] text-slate-500">{r.receipt_no || r.payment_method || "—"}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </Section>
        )}

        {/* ── Contracts ─────────────────────────────────── */}
        <Section icon={Briefcase} title="Contracts" count={data.contracts.length} testid="sec-contracts">
          {data.contracts.length === 0 ? <Empty msg="No contracts on record." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] font-mono-alt uppercase tracking-widest text-slate-500"><tr>
                  <th className="px-2 py-1.5 text-left">Type</th><th className="px-2 py-1.5 text-left">Position</th><th className="px-2 py-1.5 text-left">Project</th>
                  <th className="px-2 py-1.5 text-left">Start</th><th className="px-2 py-1.5 text-left">End</th><th className="px-2 py-1.5 text-left">Salary</th><th className="px-2 py-1.5 text-left">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.contracts.map((c) => (
                    <ClickRow key={c.id} record={c} title={`Contract · ${c.contract_type || ""}`} subtitle={`${c.position || ""} · ${formatDate(c.start_date)}`} inspector={inspector} testid={`contract-row-${c.id}`}>
                      <td className="px-2 py-1.5">{c.contract_type}</td>
                      <td className="px-2 py-1.5">{c.position || "—"}</td>
                      <td className="px-2 py-1.5">{c.project_ref || "—"}</td>
                      <td className="px-2 py-1.5">{formatDate(c.start_date)}</td>
                      <td className="px-2 py-1.5">{c.end_date ? formatDate(c.end_date) : "—"}</td>
                      <td className="px-2 py-1.5 tabular-nums">{formatCurrency(c.salary_amount)}</td>
                      <td className="px-2 py-1.5"><StatusChip s={c.status} /></td>
                    </ClickRow>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* ── Assets Held + History ─────────────────────── */}
        <Section icon={Package} title="Assets" count={asum.currently_held || 0} testid="sec-assets">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-sm">
            <Mini label="Currently Held"  value={asum.currently_held ?? 0} color="text-emerald-600" />
            <Mini label="History (Total)" value={asum.total_touched ?? 0} />
            <Mini label="Asset Requests"  value={asum.requests_total ?? 0} />
            <Mini label="Movements"       value={asum.movements ?? 0} />
          </div>
          {data.asset_history.length === 0 ? <Empty msg="No assets currently or historically assigned." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] font-mono-alt uppercase tracking-widest text-slate-500"><tr>
                  <th className="px-2 py-1.5 text-left">Asset ID</th><th className="px-2 py-1.5 text-left">Description</th>
                  <th className="px-2 py-1.5 text-left">Category</th><th className="px-2 py-1.5 text-left">Status</th>
                  <th className="px-2 py-1.5 text-left">Location</th><th className="px-2 py-1.5 text-left">Held?</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.asset_history.slice(0, 30).map((a) => (
                    <ClickRow key={a.id || a.asset_id} record={a} title={a.asset_id} subtitle={a.description} inspector={inspector} testid={`asset-row-${a.asset_id}`}>
                      <td className="px-2 py-1.5 font-mono-alt">{a.asset_id}</td>
                      <td className="px-2 py-1.5 max-w-xs truncate">{a.description}</td>
                      <td className="px-2 py-1.5">{a.category || "—"}</td>
                      <td className="px-2 py-1.5"><StatusChip s={a.status} /></td>
                      <td className="px-2 py-1.5">{a.location || "—"}</td>
                      <td className="px-2 py-1.5">{a.currently_held ? <span className="text-emerald-600 font-bold">●</span> : <span className="text-slate-400">○</span>}</td>
                    </ClickRow>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.asset_requests.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 mb-1">Asset Requests Submitted · {data.asset_requests.length}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] font-mono-alt uppercase tracking-widest text-slate-500"><tr>
                    <th className="px-2 py-1.5 text-left">Type</th><th className="px-2 py-1.5 text-left">Asset(s)</th>
                    <th className="px-2 py-1.5 text-left">Purpose</th><th className="px-2 py-1.5 text-left">Status</th><th className="px-2 py-1.5 text-left">Submitted</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.asset_requests.slice(0, 20).map((r) => (
                      <ClickRow key={r.id} record={r} title={`Asset Request · ${r.request_type}`} subtitle={r.asset_id || (r.asset_ids || []).join(", ")} inspector={inspector} testid={`asset-req-row-${r.id}`}>
                        <td className="px-2 py-1.5">{r.request_type}</td>
                        <td className="px-2 py-1.5 font-mono-alt text-[10px]">{r.asset_id || (r.asset_ids || []).join(", ") || "—"}</td>
                        <td className="px-2 py-1.5 max-w-xs truncate">{r.purpose || r.incident_details || "—"}</td>
                        <td className="px-2 py-1.5"><StatusChip s={r.status} /></td>
                        <td className="px-2 py-1.5 text-slate-500">{r.created_at?.slice(0, 10)}</td>
                      </ClickRow>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Section>

        {/* ── Warnings & Defenses ───────────────────────── */}
        <Section icon={Warning} title="Warnings & Discipline" count={data.warnings.length} testid="sec-warnings">
          {data.warnings.length === 0 ? <Empty msg="No warnings or discipline records found." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] font-mono-alt uppercase tracking-widest text-slate-500"><tr>
                  <th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Type</th>
                  <th className="px-2 py-1.5 text-left">Severity</th><th className="px-2 py-1.5 text-left">Reason</th>
                  <th className="px-2 py-1.5 text-left">Status</th><th className="px-2 py-1.5 text-left">Issued by</th>
                  <th className="px-2 py-1.5 text-right">Action</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.warnings.map((w) => {
                    const hasDefense = data.defenses.some((d) => d.warning_id === w.id);
                    const canDefend = isSelf && !hasDefense && w.status !== "closed";
                    return (
                      <ClickRow key={w.id} record={w} title={`Warning · ${w.warning_type || ""}`} subtitle={w.reason} inspector={inspector} testid={`warning-row-${w.id}`}>
                        <td className="px-2 py-1.5">{formatDate(w.issue_date)}</td>
                        <td className="px-2 py-1.5">{w.warning_type}</td>
                        <td className="px-2 py-1.5"><StatusChip s={w.severity || "—"} /></td>
                        <td className="px-2 py-1.5 max-w-xs truncate" title={w.reason}>{w.reason}</td>
                        <td className="px-2 py-1.5"><StatusChip s={w.status} /></td>
                        <td className="px-2 py-1.5 text-slate-500">{w.issued_by || "—"}</td>
                        <td className="px-2 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                          {canDefend ? (
                            <DefenseFormDialog warning={w} onSubmitted={load} />
                          ) : hasDefense ? (
                            <span className="font-mono-alt text-[10px] uppercase tracking-wider text-blue-700">Defense submitted</span>
                          ) : <span className="text-slate-400 text-[10px]">—</span>}
                        </td>
                      </ClickRow>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {data.defenses.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 mb-1">Defense Responses · {data.defenses.length}</div>
              <div className="space-y-2">
                {data.defenses.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => inspector.open(d, { title: "Defense Response", subtitle: formatDate(d.response_date || d.created_at) })}
                    className="block w-full text-left rounded-md border border-slate-200 bg-white p-2 hover:bg-slate-50"
                    data-testid={`defense-card-${d.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">{formatDate(d.response_date || d.created_at)} · for warning #{(d.warning_id || "").slice(-6)}</span>
                      <StatusChip s={d.review_status || d.status || "submitted"} />
                    </div>
                    <div className="mt-1 text-xs text-slate-700 line-clamp-2">{d.response || d.body || "—"}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* ── Ethics & Grievance (non-anonymous) ────────── */}
        {(isFull || isSelf) && (
          <Section icon={ChatCircleDots} title="Ethics & Grievance Reports" count={data.ethics.length} testid="sec-ethics">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3 text-sm">
              <Mini label="Total"        value={esum.total ?? 0} />
              <Mini label="Submitted"    value={esum.submitted ?? 0} />
              <Mini label="Under Review" value={esum.under_review ?? 0} color="text-amber-600" />
              <Mini label="Escalated"    value={esum.escalated ?? 0} color="text-rose-600" />
              <Mini label="Resolved"     value={esum.resolved ?? 0} color="text-emerald-600" />
              <Mini label="Closed"       value={esum.closed ?? 0} color="text-slate-500" />
            </div>
            {data.ethics.length === 0 ? (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <EyeSlash size={14} />
                <span>No non-anonymous ethics or grievance reports linked to this employee. Anonymous reports remain confidential and are NEVER exposed here.</span>
              </div>
            ) : (
              <div className="space-y-2">
                {data.ethics.slice(0, 10).map((e) => (
                  <button
                    key={e.id}
                    onClick={() => inspector.open(e, { title: e.title || `Ethics Case #${(e.id || "").slice(-6)}`, subtitle: e.category })}
                    className="block w-full text-left rounded-md border border-slate-200 bg-white p-2 hover:bg-slate-50"
                    data-testid={`ethics-card-${e.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm text-slate-900 truncate">{e.title || `Case #${(e.id || "").slice(-6)}`}</span>
                      <StatusChip s={e.status} />
                    </div>
                    <div className="mt-0.5 text-[10px] text-slate-500">{e.category || "—"} · submitted {formatDateTime(e.submitted_at || e.created_at)}</div>
                  </button>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* ── Leave & Attendance ────────────────────────── */}
        <Section icon={CalendarBlank} title="Leave & Attendance" count={data.leaves.length} testid="sec-leave-attendance">
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-2 mb-3 text-sm">
            <Mini label="Present"       value={data.performance?.attendance?.present ?? 0} color="text-emerald-600" />
            <Mini label="Late"          value={data.performance?.attendance?.late ?? 0} color="text-amber-600" />
            <Mini label="Absent"        value={data.performance?.attendance?.absent ?? 0} color="text-rose-600" />
            <Mini label="Leave (Appr.)" value={data.performance?.attendance?.leave_days_approved ?? 0} />
            <Mini label="Att. Rate"     value={`${data.performance?.attendance?.rate ?? 0}%`} color="text-blue-600" />
          </div>
          {data.leaves.length === 0 ? <Empty msg="No leave on record." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] font-mono-alt uppercase tracking-widest text-slate-500"><tr>
                  <th className="px-2 py-1.5 text-left">Type</th><th className="px-2 py-1.5 text-left">From</th>
                  <th className="px-2 py-1.5 text-left">To</th><th className="px-2 py-1.5 text-left">Days</th><th className="px-2 py-1.5 text-left">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.leaves.map((lv) => (
                    <ClickRow key={lv.id} record={lv} title={`Leave · ${lv.leave_type || ""}`} subtitle={`${formatDate(lv.start_date)} → ${formatDate(lv.end_date)}`} inspector={inspector} testid={`leave-row-${lv.id}`}>
                      <td className="px-2 py-1.5">{lv.leave_type}</td>
                      <td className="px-2 py-1.5">{formatDate(lv.start_date)}</td>
                      <td className="px-2 py-1.5">{formatDate(lv.end_date)}</td>
                      <td className="px-2 py-1.5 tabular-nums">{lv.days || "—"}</td>
                      <td className="px-2 py-1.5"><StatusChip s={lv.status} /></td>
                    </ClickRow>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* ── Tasks & Performance ───────────────────────── */}
        <Section icon={ChartBar} title="Tasks & Performance" count={data.performance?.total_tasks ?? 0} testid="sec-performance">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-3 text-sm">
            <Mini label="Total"        value={data.performance?.total_tasks ?? 0} />
            <Mini label="Completed"    value={data.performance?.completed ?? 0} color="text-emerald-600" />
            <Mini label="Overdue"      value={data.performance?.overdue ?? 0} color={data.performance?.overdue ? "text-rose-600" : "text-slate-900"} />
            <Mini label="On-time %"    value={`${data.performance?.on_time_rate ?? 0}%`} color="text-blue-600" />
            <Mini label="Quality %"    value={`${data.performance?.quality_rate ?? 0}%`} color="text-violet-600" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 md:grid-cols-10 gap-1 mb-3 text-[10px]" data-testid="hr-task-breakdown">
            {Object.entries(tbreak).map(([k, v]) => (
              <div key={k} className="rounded border border-slate-200 bg-white px-1.5 py-1">
                <div className="text-[8.5px] uppercase tracking-wider text-slate-500 truncate">{k.replace(/_/g, " ")}</div>
                <div className="font-mono-alt text-xs font-bold text-slate-900">{v ?? 0}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-3 text-sm">
            <Mini label="Logged Hours"   value={effort.total_hours ?? 0} />
            <Mini label="Billable"        value={effort.billable_hours ?? 0} color="text-emerald-600" />
            <Mini label="Approved Hours"  value={effort.approved_hours ?? 0} color="text-blue-600" />
            <Mini label="Billable %"      value={`${effort.billable_pct ?? 0}%`} color="text-violet-600" />
            <Mini label="TS Approval %"   value={`${effort.approval_pct ?? 0}%`} color="text-amber-600" />
          </div>
          <ScoreBreakdown breakdown={data.score_breakdown} overall={data.overall_score} rating={data.performance?.rating} />
          {data.tasks.length === 0 ? <Empty msg="No tasks assigned." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] font-mono-alt uppercase tracking-widest text-slate-500"><tr>
                  <th className="px-2 py-1.5 text-left">Task</th><th className="px-2 py-1.5 text-left">Status</th>
                  <th className="px-2 py-1.5 text-left">Review</th><th className="px-2 py-1.5 text-left">Due</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.tasks.slice(0, 15).map((t) => (
                    <ClickRow key={t.id} record={t} title={t.title} subtitle={t.status} inspector={inspector} testid={`task-row-${t.id}`}>
                      <td className="px-2 py-1.5 max-w-xs truncate">{t.title}</td>
                      <td className="px-2 py-1.5"><StatusChip s={t.status} /></td>
                      <td className="px-2 py-1.5"><StatusChip s={t.review_status || "—"} /></td>
                      <td className="px-2 py-1.5">{t.due_date ? formatDate(t.due_date) : "—"}</td>
                    </ClickRow>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* ── Separation ────────────────────────────────── */}
        {data.separations.length > 0 && (
          <Section icon={ShieldCheck} title="Separation / Exit" count={data.separations.length} testid="sec-separation">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] font-mono-alt uppercase tracking-widest text-slate-500"><tr>
                  <th className="px-2 py-1.5 text-left">Type</th><th className="px-2 py-1.5 text-left">Effective</th>
                  <th className="px-2 py-1.5 text-left">Reason</th><th className="px-2 py-1.5 text-left">Clearance</th><th className="px-2 py-1.5 text-left">Handover</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.separations.map((sep) => (
                    <ClickRow key={sep.id} record={sep} title={`Separation · ${sep.separation_type || ""}`} subtitle={formatDate(sep.effective_date)} inspector={inspector} testid={`separation-row-${sep.id}`}>
                      <td className="px-2 py-1.5">{sep.separation_type}</td>
                      <td className="px-2 py-1.5">{formatDate(sep.effective_date)}</td>
                      <td className="px-2 py-1.5 max-w-xs truncate">{sep.reason}</td>
                      <td className="px-2 py-1.5"><StatusChip s={sep.clearance_status} /></td>
                      <td className="px-2 py-1.5"><StatusChip s={sep.handover_status} /></td>
                    </ClickRow>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* ── Audit Trail timeline ───────────────────────── */}
        {isFull && data.audit && data.audit.length > 0 && (
          <Section icon={Eye} title="Audit Trail" count={data.audit.length} testid="sec-audit">
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {data.audit.slice(0, 50).map((a, idx) => (
                <div key={a.id || idx} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs" data-testid={`audit-${a.id || idx}`}>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">{formatDateTime(a.created_at)}</span>
                    <span className="font-mono-alt text-[10px] uppercase tracking-wider text-blue-700">{a.action}</span>
                    <span className="text-slate-700">{a.user_email || "system"}</span>
                  </div>
                  {a.diff && a.diff.length > 0 && (
                    <div className="mt-1.5 space-y-0.5">
                      {a.diff.slice(0, 4).map((d, i) => (
                        <div key={i} className="text-[11px] text-slate-600">
                          <span className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">{d.field}: </span>
                          <span className="font-mono text-rose-700 line-through">{String(d.from ?? "—")}</span>
                          <span className="mx-1 text-slate-400">→</span>
                          <span className="font-mono text-emerald-700">{String(d.to ?? "—")}</span>
                        </div>
                      ))}
                      {a.diff.length > 4 && <div className="text-[10px] text-slate-400">+{a.diff.length - 4} more field change(s)…</div>}
                    </div>
                  )}
                  {a.comments && <div className="mt-1 text-[11px] italic text-slate-500">{`"${a.comments}"`}</div>}
                </div>
              ))}
              {data.audit.length > 50 && <div className="text-[10px] text-slate-400 text-center">Showing first 50 of {data.audit.length} entries.</div>}
            </div>
          </Section>
        )}

        <div className="mt-6 text-[10px] font-mono-alt uppercase tracking-widest text-slate-400 text-center">
          Descon Management System · {data.score_label} · Generated {new Date().toLocaleString()}
        </div>
      </div>

      <RecordInspector {...inspector.props} />
    </>
  );
}
